import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Label, Datepicker } from 'flowbite-react';
import { FiSearch, FiAlertTriangle, FiCheckCircle, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { v4 as uuidv4 } from 'uuid';
import * as wasm from 'sched';
import { useWorkspace } from '../../context/WorkspaceContext';
import type { DraftAllocation } from '../../hooks/usePlanningGrid';

/**
 * Modal for creating a new resource allocation from the planning grid.
 *
 * Surfaces tasks ranked by the team's priority list, walked topologically
 * within each priority. Each candidate carries progress signals (planned
 * effort, P(start)/P(finish) by the period end, blocker count) so the user
 * can pick a meaningful next thing to schedule rather than scanning a flat
 * alphabetical list.
 *
 * Sections:
 *   - Highest priority section with at least one matched, non-completed
 *     task is expanded by default. Others are collapsed.
 *   - Sections with zero matched non-completed tasks are dimmed and labeled
 *     "no matches" so the user understands why the section is empty.
 *
 * Within each section:
 *   - Person-unmatched tasks are dimmed with a hazard icon.
 *   - Completed tasks are sorted to the bottom, dimmed.
 *
 * Search box queries across the same dataset; results are flat and ignore
 * priority structure (the user can schedule whatever they want).
 */

function fmtEffort(tenths: number): string {
  if (tenths === 0) return '0';
  const days = tenths / 10;
  return days % 1 === 0 ? `${days}d` : `${days.toFixed(1)}d`;
}

/**
 * Parse an ISO `YYYY-MM-DD` into a local-midnight Date for the Datepicker.
 * Using local midnight (not UTC) avoids the off-by-one display in negative
 * timezones — the Datepicker reads the calendar fields, not the timestamp.
 */
function isoToDate(iso: string): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Inverse of `isoToDate` — emits ISO `YYYY-MM-DD` from local calendar fields. */
function dateToIso(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fmtPct(p: number | null | undefined): string | null {
  if (p === null || p === undefined) return null;
  if (p <= 0) return '0%';
  if (p >= 1) return '100%';
  if (p < 0.01) return '<1%';
  return `${Math.round(p * 100)}%`;
}

/**
 * Map a probability to a natural-language readiness headline. We want the
 * row to read like a human assessment ("Should be done by then", "Probably
 * still blocked") rather than a stat ("P(done) 87%"). The numeric percentage
 * is still shown alongside as a subtler signal of confidence.
 *
 * `kind`:
 *   - 'done':  P(finish ≤ target). Used when work is already partly planned.
 *   - 'start': P(start ≤ target). Used when no work is planned yet — we want
 *              to know whether the task is reachable by the target date.
 *
 * Returns `null` when there's no probability to express (no fuzzy schedule
 * for this task; the row should fall back to just the effort labels).
 */
function readinessPhrase(
  p: number | null | undefined,
  kind: 'done' | 'start',
): { headline: string; tone: 'good' | 'ok' | 'warn' | 'bad' } | null {
  if (p === null || p === undefined) return null;
  if (kind === 'done') {
    if (p >= 0.95) return { headline: 'Should be done by then', tone: 'good' };
    if (p >= 0.75) return { headline: 'Likely done', tone: 'good' };
    if (p >= 0.5) return { headline: 'Maybe done', tone: 'ok' };
    if (p >= 0.25) return { headline: 'Probably still in progress', tone: 'warn' };
    return { headline: 'Almost certainly still in progress', tone: 'bad' };
  }
  if (p >= 0.95) return { headline: 'Should be ready to start', tone: 'good' };
  if (p >= 0.75) return { headline: 'Likely ready to start', tone: 'good' };
  if (p >= 0.5) return { headline: 'Maybe ready to start', tone: 'ok' };
  if (p >= 0.25) return { headline: 'Probably still blocked', tone: 'warn' };
  return { headline: 'Almost certainly still blocked', tone: 'bad' };
}

const TONE_TEXT_CLASS: Record<'good' | 'ok' | 'warn' | 'bad', string> = {
  good: 'text-cm-accent-teal',
  ok: 'text-cm-text-secondary',
  warn: 'text-amber-400',
  bad: 'text-red-400',
};

interface Props {
  personId: string;
  personName: string;
  teamId: string;
  startDate: string;
  endDate: string;
  /** Capacity per day (tenths) for the person, keyed by day-of-window index. */
  personCells: Record<string, any>;
  /** All days in the window — used for capacity calculations and target_date. */
  days: { date: string }[];
  database: any;
  /** In-flight planning-grid drafts so candidate_tasks counts them too. */
  drafts: DraftAllocation[];
  applyCommand: (command: wasm.DatabaseCommand) => Promise<void>;
  onClose: () => void;
}

export const AllocationCreateModal: React.FC<Props> = ({
  personId,
  personName,
  teamId,
  startDate,
  endDate,
  personCells,
  days,
  database,
  drafts,
  applyCommand,
  onClose,
}) => {
  const { safeData } = useWorkspace();

  const [report, setReport] = useState<wasm.CandidateTasksReport | null>(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportError, setReportError] = useState<string | null>(null);

  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskTitle, setTaskTitle] = useState<string | null>(null);
  const [effort, setEffort] = useState<number>(10);
  const [start, setStart] = useState<string>(startDate);
  const [end, setEnd] = useState<string>(endDate);
  const [search, setSearch] = useState<string>('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Pull the candidate-tasks report once when the modal opens (or when the
  // window/draft set changes — close-and-reopen doesn't fire effects).
  useEffect(() => {
    if (!safeData) return;
    let cancelled = false;
    setReportLoading(true);
    const draftDtos = drafts.map((d) => ({
      entity: d.entity,
      person_id: d.personId,
      effort: d.effort,
      when_start: d.whenStart,
      when_end: d.whenEnd,
    }));
    wasm
      .candidate_tasks(safeData, teamId, personId, end, draftDtos as any)
      .then((res) => {
        if (cancelled) return;
        setReport(res as wasm.CandidateTasksReport);
        setReportError(null);
        // Auto-expand the first priority section that has at least one
        // matched, non-completed task. Everything else stays collapsed.
        const firstUseful = (res as wasm.CandidateTasksReport).priority_sections.find(
          (s) => s.tasks.some((t) => t.matches_person && !t.completed),
        );
        if (firstUseful?.priority_item_id) {
          setExpanded({ [firstUseful.priority_item_id]: true });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('candidate_tasks failed:', e);
        setReportError(e?.message || String(e));
      })
      .finally(() => {
        if (!cancelled) setReportLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [safeData, teamId, personId, end, drafts]);

  // Capacity numbers used by the "quick effort" recommendation buttons.
  // - fullCapacity: total capacity (in tenths) the person has across the
  //   chosen window — i.e., what's available if no other work were assigned.
  // - remainingCapacity: capacity minus already-committed effort across all
  //   tasks. Tells the user "how much can I still spend on this person?"
  const { fullCapacity, remainingCapacity } = useMemo(() => {
    let cap = 0;
    let used = 0;
    for (let i = 0; i < days.length; i++) {
      const d = days[i];
      if (d.date >= start && d.date <= end) {
        const cell = personCells[i.toString()];
        if (cell) {
          cap += cell.capacity ?? 0;
          used += cell.utilized ?? 0;
        }
      }
    }
    return { fullCapacity: cap, remainingCapacity: Math.max(0, cap - used) };
  }, [days, personCells, start, end]);

  // Flatten all tasks across all sections for the search view (deduped by id).
  const allTasksById = useMemo(() => {
    const out: Record<string, wasm.CandidateTask> = {};
    if (!report) return out;
    for (const s of report.priority_sections) {
      for (const t of s.tasks) out[t.id] = t;
    }
    for (const t of report.unprioritized_section.tasks) {
      if (!out[t.id]) out[t.id] = t;
    }
    return out;
  }, [report]);

  const searchResults = useMemo(() => {
    if (!search.trim() || !report) return [] as wasm.CandidateTask[];
    const q = search.trim().toLowerCase();
    const matched = Object.values(allTasksById).filter((t) =>
      t.title.toLowerCase().includes(q),
    );
    return sortTasks(matched);
  }, [allTasksById, search, report]);

  const handleSubmit = async () => {
    if (!taskId) return;
    const epoch =
      start === end
        ? ({ type: 'Day' as const, day: start })
        : ({ type: 'Days' as const, start_inclusive: start, end_inclusive: end });
    await applyCommand({
      type: 'IncrementResourceAllocation',
      id: uuidv4(),
      entity: taskId,
      person_id: personId,
      increment: effort,
      when: epoch,
    } as wasm.DatabaseCommand);
    onClose();
  };

  const selectedSummary = taskTitle ?? null;

  // Look up the full CandidateTask for the selected id, so the detail panel
  // can render scheduled-effort, readiness, and blockers without re-querying.
  const selectedTask = taskId ? allTasksById[taskId] ?? null : null;

  return (
    <Modal show onClose={onClose} size="2xl">
      <ModalHeader>Schedule work for {personName}</ModalHeader>
      {/*
        `!overflow-visible` overrides flowbite-react's default `overflow-auto`
        on ModalBody — without it, the Datepicker popup (and any other
        absolutely-positioned child that extends beyond the body) is clipped.
        The modal as a whole still scrolls via the root container's
        `overflow-y-auto`, so this doesn't strand long content offscreen.
      */}
      <ModalBody className="!overflow-visible">
        <div className="space-y-4">
          {/* Task selection — either the chosen task's detail panel, or the picker UI */}
          {taskId ? (
            <SelectedTaskDetail
              taskId={taskId}
              fallbackTitle={taskTitle ?? selectedSummary ?? '(unknown task)'}
              candidate={selectedTask}
              database={database}
              onClear={() => {
                setTaskId(null);
                setTaskTitle(null);
              }}
            />
          ) : (
            <>
              {/* Search */}
              <div className="relative">
                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-cm-text-muted" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search any task…"
                  className="w-full text-xs bg-cm-bg-deep border border-cm-border-subtle rounded pl-8 pr-2 py-1.5 text-cm-text-primary"
                />
              </div>

              {/* Results panel */}
              <div className="max-h-[420px] overflow-y-auto border border-cm-border-subtle rounded-lg">
                {reportLoading && (
                  <div className="p-3 text-xs text-cm-text-muted">Loading suggestions…</div>
                )}
                {reportError && (
                  <div className="p-3 text-xs text-red-400">Error: {reportError}</div>
                )}

                {!reportLoading && !reportError && report && search.trim() && (
                  <SearchView
                    results={searchResults}
                    onPick={(t) => {
                      setTaskId(t.id);
                      setTaskTitle(t.title);
                      setSearch('');
                    }}
                  />
                )}

                {!reportLoading && !reportError && report && !search.trim() && (
                  <SectionsView
                    sections={[
                      ...report.priority_sections,
                      // The unprioritized bucket is rendered last; treat it
                      // like a normal section but with a synthetic id so
                      // the expand toggle has something to key on.
                      { ...report.unprioritized_section, priority_item_id: '__unprioritized__' },
                    ]}
                    expanded={expanded}
                    setExpanded={setExpanded}
                    onPick={(t) => {
                      setTaskId(t.id);
                      setTaskTitle(t.title);
                    }}
                  />
                )}
              </div>
            </>
          )}

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-cm-text-muted mb-1 block">From</Label>
              <Datepicker
                value={isoToDate(start)}
                onChange={(d) => {
                  if (d) setStart(dateToIso(d));
                }}
                language="en-GB"
                weekStart={2 /* Monday */}
                showTodayButton
              />
            </div>
            <div>
              <Label className="text-[10px] text-cm-text-muted mb-1 block">To</Label>
              <Datepicker
                value={isoToDate(end)}
                onChange={(d) => {
                  if (d) setEnd(dateToIso(d));
                }}
                language="en-GB"
                weekStart={2}
                showTodayButton
                minDate={isoToDate(start) ?? undefined}
              />
            </div>
          </div>

          {/* Effort */}
          <div>
            <Label className="text-[10px] text-cm-text-muted mb-1 block">
              Effort <span className="font-mono">{fmtEffort(effort)}</span>
            </Label>
            <input
              type="range"
              min={1}
              max={Math.max(100, fullCapacity)}
              step={1}
              value={effort}
              onChange={(e) => setEffort(parseInt(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-gray-700
                         [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5
                         [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full
                         [&::-webkit-slider-thumb]:bg-cm-accent-blue"
            />
            <EffortRecommendations
              effort={effort}
              setEffort={setEffort}
              selectedTask={selectedTask}
              personName={personName}
              fullCapacity={fullCapacity}
              remainingCapacity={remainingCapacity}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button color="gray" size="xs" onClick={onClose}>
              Cancel
            </Button>
            <Button size="xs" onClick={handleSubmit} disabled={!taskId}>
              Schedule
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

// ── Sub-views ──────────────────────────────────────────────────────────────

/**
 * Stable sort: completed tasks last (with original ordering preserved within
 * the not-completed and completed groups, since the Rust side already sorts
 * them topologically + alphabetically).
 */
function sortTasks(tasks: wasm.CandidateTask[]): wasm.CandidateTask[] {
  const notDone: wasm.CandidateTask[] = [];
  const done: wasm.CandidateTask[] = [];
  for (const t of tasks) (t.completed ? done : notDone).push(t);
  return [...notDone, ...done];
}

function SectionsView({
  sections,
  expanded,
  setExpanded,
  onPick,
}: {
  sections: wasm.PrioritySection[];
  expanded: Record<string, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  onPick: (t: wasm.CandidateTask) => void;
}) {
  if (sections.every((s) => s.tasks.length === 0)) {
    return (
      <div className="p-3 text-xs text-cm-text-muted">
        No prioritized work for this team yet.
      </div>
    );
  }
  return (
    <div className="divide-y divide-cm-border-subtle">
      {sections.map((section) => {
        const id = section.priority_item_id ?? '__unprioritized__';
        const matchedNotDone = section.tasks.filter((t) => t.matches_person && !t.completed).length;
        const dimmed = matchedNotDone === 0;
        const isOpen = !!expanded[id];
        return (
          <div key={id} className={dimmed ? 'opacity-60' : ''}>
            <button
              type="button"
              onClick={() =>
                setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
              }
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-cm-bg-deep/40"
            >
              {isOpen ? (
                <FiChevronDown className="w-3 h-3 text-cm-text-muted" />
              ) : (
                <FiChevronRight className="w-3 h-3 text-cm-text-muted" />
              )}
              <span className="font-mono text-cm-text-secondary truncate flex-1">
                {section.label}
              </span>
              <span className="text-[10px] text-cm-text-muted">
                {matchedNotDone > 0 ? `${matchedNotDone} match${matchedNotDone === 1 ? '' : 'es'}` : 'no matches'}
              </span>
            </button>
            {isOpen && (
              <ul>
                {sortTasks(section.tasks).map((t) => (
                  <CandidateTaskRow key={t.id} task={t} onPick={onPick} />
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SearchView({
  results,
  onPick,
}: {
  results: wasm.CandidateTask[];
  onPick: (t: wasm.CandidateTask) => void;
}) {
  if (results.length === 0) {
    return (
      <div className="p-3 text-xs text-cm-text-muted">No tasks match that search.</div>
    );
  }
  return (
    <ul>
      {results.map((t) => (
        <CandidateTaskRow key={t.id} task={t} onPick={onPick} />
      ))}
    </ul>
  );
}

function CandidateTaskRow({
  task,
  onPick,
}: {
  task: wasm.CandidateTask;
  onPick: (t: wasm.CandidateTask) => void;
}) {
  const dimmed = task.completed || !task.matches_person;
  const planned = task.planned_effort_tenths ?? 0;
  const estimate = task.estimate_tenths ?? null;

  // Pick the right probability question to answer:
  // - "Will it be done?" — if some work is already planned for it.
  // - "Will it be ready to start?" — otherwise.
  // Already-completed tasks short-circuit to a fixed phrase.
  const phrase = task.completed
    ? { headline: 'Already complete', tone: 'good' as const }
    : readinessPhrase(
        planned > 0 ? task.p_finish_by_target : task.p_start_by_target,
        planned > 0 ? 'done' : 'start',
      );
  const probValue = task.completed
    ? null
    : fmtPct(planned > 0 ? task.p_finish_by_target : task.p_start_by_target);

  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(task)}
        className={`w-full px-3 py-2 text-left text-xs hover:bg-cm-bg-deep/40 transition-colors ${
          dimmed ? 'text-cm-text-muted' : 'text-cm-text-primary'
        }`}
      >
        <div className="flex items-center gap-2">
          {task.completed && (
            <FiCheckCircle className="w-3 h-3 text-cm-accent-teal shrink-0" title="Completed" />
          )}
          {!task.matches_person && (
            <FiAlertTriangle
              className="w-3 h-3 text-amber-400 shrink-0"
              title="Person is not matched to this work."
            />
          )}
          <span className="truncate flex-1 font-medium">{task.title}</span>
          {phrase && (
            <span className={`text-[10px] shrink-0 ${TONE_TEXT_CLASS[phrase.tone]}`}>
              {phrase.headline}
              {probValue && (
                <span className="ml-1 font-mono text-cm-text-muted">({probValue})</span>
              )}
            </span>
          )}
        </div>
        <div className="ml-5 mt-0.5 flex items-center gap-3 text-[10px] text-cm-text-muted">
          {/*
            Numbers labelled inline so the row doesn't read as bare math.
            "0.3d / 0.3d scheduled" reads as "0.3 of 0.3 days scheduled".
          */}
          {estimate !== null ? (
            <span>
              {fmtEffort(planned)} / {fmtEffort(estimate)} scheduled
            </span>
          ) : (
            <span>{fmtEffort(planned)} scheduled · no estimate</span>
          )}
          {task.incomplete_blockers.length > 0 && (
            <span
              className="inline-flex items-center gap-1 text-amber-400"
              title={`Blocked by:\n${task.incomplete_blockers.map((b) => `${b.entity_type}: ${b.title}`).join('\n')}`}
            >
              <FiAlertTriangle className="w-3 h-3" />
              {task.incomplete_blockers.length} blocker
              {task.incomplete_blockers.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </button>
    </li>
  );
}

/**
 * Detail panel that replaces the simple "selected-task chip." Surfaces:
 *  - Title + description from the database.
 *  - Readiness phrase (with the percentage, since at this size we can afford
 *    to be explicit) sized prominently so the user sees the answer to
 *    "is this thing reachable by my target date?"
 *  - Scheduled-vs-estimate effort with explicit labels.
 *  - The full list of incomplete blocker titles — the row's hover tooltip
 *    is hard to read for more than one or two; here we just show them.
 *  - "Person is not matched" notice when applicable.
 */
function SelectedTaskDetail({
  taskId,
  fallbackTitle,
  candidate,
  database,
  onClear,
}: {
  taskId: string;
  fallbackTitle: string;
  candidate: wasm.CandidateTask | null;
  database: any;
  onClear: () => void;
}) {
  // Pull description directly from the database; the candidate DTO doesn't
  // include it (the search list doesn't need it).
  const description = (() => {
    if (!database) return null;
    try {
      const t = database.tasks?.()?.[taskId];
      const d: string | null | undefined = t?.description?.();
      return d || null;
    } catch {
      return null;
    }
  })();

  const planned = candidate?.planned_effort_tenths ?? 0;
  const estimate = candidate?.estimate_tenths ?? null;
  const phrase = candidate?.completed
    ? { headline: 'Already complete', tone: 'good' as const }
    : readinessPhrase(
        planned > 0 ? candidate?.p_finish_by_target : candidate?.p_start_by_target,
        planned > 0 ? 'done' : 'start',
      );
  const probValue = candidate?.completed
    ? null
    : fmtPct(planned > 0 ? candidate?.p_finish_by_target : candidate?.p_start_by_target);

  return (
    <div className="border border-cm-border-subtle rounded-lg p-4 space-y-3 bg-cm-bg-deep/40">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-medium text-cm-text-primary">
          {candidate?.title ?? fallbackTitle}
        </h3>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-cm-text-muted hover:text-cm-text-primary shrink-0"
        >
          change
        </button>
      </div>

      {description && (
        <p className="text-xs text-cm-text-secondary whitespace-pre-wrap line-clamp-4">
          {description}
        </p>
      )}

      {!candidate?.matches_person && candidate && (
        <p className="text-[11px] text-amber-400 inline-flex items-center gap-1.5">
          <FiAlertTriangle className="w-3 h-3" />
          This person isn't matched to this work — scheduling them anyway is allowed.
        </p>
      )}

      {phrase && (
        <div className="flex items-baseline gap-2">
          <span className={`text-sm font-medium ${TONE_TEXT_CLASS[phrase.tone]}`}>
            {phrase.headline}
          </span>
          {probValue && (
            <span className="text-xs font-mono text-cm-text-muted">({probValue})</span>
          )}
        </div>
      )}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
        <dt className="text-cm-text-muted">Already scheduled</dt>
        <dd className="text-cm-text-primary font-mono">{fmtEffort(planned)}</dd>
        <dt className="text-cm-text-muted">Estimate</dt>
        <dd className="text-cm-text-primary font-mono">
          {estimate !== null ? fmtEffort(estimate) : 'none'}
        </dd>
      </dl>

      {candidate && candidate.incomplete_blockers.length > 0 && (
        <div>
          <p className="text-[11px] text-amber-400 mb-1.5 inline-flex items-center gap-1.5">
            <FiAlertTriangle className="w-3 h-3" />
            Currently blocked by (direct dependencies only):
          </p>
          <BlockerTable blockers={candidate.incomplete_blockers} />
        </div>
      )}
    </div>
  );
}

/**
 * Lightweight three-column table for the selected-task detail panel:
 * type · title · expected finish date (with p10/p90 in a hover tooltip when
 * available). "—" for blockers that aren't in the fuzzy schedule.
 */
function BlockerTable({ blockers }: { blockers: wasm.Blocker[] }) {
  return (
    <table className="w-full text-[11px] border-separate border-spacing-y-0.5">
      <thead>
        <tr className="text-cm-text-muted">
          <th className="text-left font-mono font-normal pr-3 w-20">Type</th>
          <th className="text-left font-mono font-normal pr-3">Title</th>
          <th className="text-left font-mono font-normal w-28">Expected finish</th>
        </tr>
      </thead>
      <tbody>
        {blockers.map((b) => {
          const range =
            b.finish_p10_date && b.finish_p90_date
              ? `${b.finish_p10_date} → ${b.finish_p90_date} (p10–p90)`
              : null;
          return (
            <tr key={`${b.entity_type}-${b.id}`} className="text-cm-text-secondary">
              <td className="pr-3 font-mono text-cm-text-muted">{b.entity_type}</td>
              <td className="pr-3 truncate" title={b.title}>{b.title}</td>
              <td
                className="font-mono text-cm-text-muted"
                title={range ?? undefined}
              >
                {b.expected_finish_date ?? '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * Quick-pick effort buttons. We compute up to four candidates:
 *   1. "Remaining on task"   — task estimate minus already-planned effort.
 *   2. "Person's free time"  — capacity minus already-utilized in window.
 *   3. "Full cycle"          — total capacity in the chosen window.
 *   4. "1 day"                — flat 10 tenths.
 * Candidates with a non-positive value (or no estimate, for #1) drop out;
 * duplicates are deduped by value so the user sees a tidy row of 1–4 buttons.
 */
function possessive(name: string): string {
  return name.endsWith('s') ? `${name}'` : `${name}'s`;
}

function EffortRecommendations({
  effort,
  setEffort,
  selectedTask,
  personName,
  fullCapacity,
  remainingCapacity,
}: {
  effort: number;
  setEffort: (n: number) => void;
  selectedTask: wasm.CandidateTask | null;
  personName: string;
  fullCapacity: number;
  remainingCapacity: number;
}) {
  // `showValue: false` on a candidate means the label already conveys its
  // value (e.g. "1 day"), so we skip the redundant `: 1d` suffix.
  const candidates: { label: string; value: number; showValue?: boolean }[] = [];

  if (selectedTask && selectedTask.estimate_tenths !== null && selectedTask.estimate_tenths !== undefined) {
    const remaining = selectedTask.estimate_tenths - (selectedTask.planned_effort_tenths ?? 0);
    if (remaining > 0) {
      candidates.push({ label: 'Remaining on task', value: remaining });
    }
  }
  if (remainingCapacity > 0) {
    candidates.push({ label: `${possessive(personName)} free time`, value: remainingCapacity });
  }
  if (fullCapacity > 0) {
    candidates.push({ label: 'Full cycle', value: fullCapacity });
  }
  candidates.push({ label: '1 day', value: 10, showValue: false });

  // Dedupe by value, preserving the first label for each value (so the more
  // specific labels — like "Remaining on task" — win over generic ones).
  const seen = new Set<number>();
  const deduped = candidates.filter((c) => {
    if (seen.has(c.value)) return false;
    seen.add(c.value);
    return true;
  });

  if (deduped.length === 0) return null;

  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {deduped.map((c) => {
        const active = effort === c.value;
        return (
          <button
            key={c.label}
            type="button"
            onClick={() => setEffort(c.value)}
            className={`px-2 py-0.5 rounded text-[10px] transition-colors ${
              active
                ? 'bg-cm-accent-blue/20 text-cm-accent-blue border border-cm-accent-blue/40'
                : 'bg-cm-bg-deep text-cm-text-muted border border-cm-border-subtle hover:border-cm-border-mid'
            }`}
          >
            {c.label}
            {c.showValue !== false && `: ${fmtEffort(c.value)}`}
          </button>
        );
      })}
    </div>
  );
}

export default AllocationCreateModal;
